/**
 * ChatPage — FE-007, FE-010, FE-011, FE-014, FE-016
 *
 * Active video chat view with:
 * - Responsive layout (320px portrait, 768px side-by-side, 1024px+ cinematic)
 * - Remote video: full-screen background with object-fit cover
 * - Local video PiP: draggable picture-in-picture
 * - Stage-appropriate visuals (avatar/blur/clear)
 * - Smooth stage transitions with CSS transitions
 * - Progress bar (3-segment with smooth fill), top bar, bottom control bar
 * - Stage transition overlays (blur reveal, glow pulse)
 * - Stage 3 reveal button with dual opt-in flow
 * - Reveal celebration overlay
 * - Connection state overlays
 * - Full control bar with Next, Mute, Camera Toggle, Report, End
 * - Toast notification system
 * - Accessibility: ARIA labels, keyboard navigable
 */
import {
  type Component,
  createSignal,
  createEffect,
  onCleanup,
  Show,
} from 'solid-js';
import type { SessionManager } from '../services/session-manager';
import { createStageManager } from '../services/stage-manager';
import type { ReportCategory } from './ReportSheet';
import ControlBar from './ControlBar';
import ConnectionStateOverlay from './ConnectionStateOverlay';
import ProgressBar from './ProgressBar';
import StageTransition from './StageTransition';
import RevealButton from './RevealButton';
import RevealCelebration from './RevealCelebration';
import { ToastContainer } from './Toast';
import { IconUser } from './Icons';
import styles from './ChatPage.module.css';

export interface ChatPageProps {
  session: SessionManager;
  localStream: MediaStream | null;
  onEnd: () => void;
}

/**
 * ChatPage — Active video chat view.
 */
const ChatPage: Component<ChatPageProps> = (props) => {
  let remoteVideoRef: HTMLVideoElement | undefined;
  let localVideoRef: HTMLVideoElement | undefined;
  let localPipRef: HTMLDivElement | undefined;

  // --- State ---
  const [showDebug, setShowDebug] = createSignal(false);
  const [elapsed, setElapsed] = createSignal(0);
  const [isFindingNext, setIsFindingNext] = createSignal(false);
  const [isCameraOff, setIsCameraOff] = createSignal(false);

  // Draggable PiP state
  const [pipPosition, setPipPosition] = createSignal<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = createSignal(false);
  let dragStartOffset = { x: 0, y: 0 };

  // --- Stage Manager (FE-010) ---
  const stageManager = createStageManager(props.session);

  // Wire reveal waiting/accepted signals from session to stage manager
  createEffect(() => {
    if (props.session.revealWaiting()) {
      stageManager.handleRevealWaiting();
    }
  });

  createEffect(() => {
    if (props.session.revealAccepted()) {
      stageManager.handleRevealAccepted();
    }
  });

  // --- Timer ---
  const startTime = Date.now();
  const timer = setInterval(() => {
    setElapsed(Math.floor((Date.now() - startTime) / 1000));
  }, 1000);
  onCleanup(() => clearInterval(timer));

  // --- Bind remote stream to video element ---
  createEffect(() => {
    const stream = props.session.remoteStream();
    if (remoteVideoRef && stream) {
      remoteVideoRef.srcObject = stream;
    } else if (remoteVideoRef && !stream) {
      remoteVideoRef.srcObject = null;
    }
  });

  // --- Bind local stream to preview video element ---
  createEffect(() => {
    if (localVideoRef && props.localStream) {
      localVideoRef.srcObject = props.localStream;
    }
  });

  // --- Watch for camera state changes (from ControlBar) ---
  // ControlBar toggles track.enabled directly, so we poll to detect changes.
  const cameraCheckInterval = setInterval(() => {
    const stream = props.localStream;
    if (stream) {
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length > 0) {
        setIsCameraOff(!videoTracks[0].enabled);
      }
    }
  }, 300);
  onCleanup(() => clearInterval(cameraCheckInterval));

  // --- Reset isFindingNext when state changes away from waiting ---
  createEffect(() => {
    const state = props.session.sessionState();
    if (state !== 'waiting' && isFindingNext()) {
      setIsFindingNext(false);
    }
  });

  // --- Derived state ---
  const stageLabel = () => {
    switch (props.session.currentStage()) {
      case 1:
        return 'Voice Only';
      case 2:
        return 'Filtered Video';
      case 3:
        return 'Clear Video';
      default:
        return 'Unknown';
    }
  };

  const stateLabel = () => {
    switch (props.session.sessionState()) {
      case 'waiting':
        return 'Finding someone...';
      case 'matched':
        return 'Matched!';
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return 'Connected';
      case 'ended':
        return 'Session ended';
      case 'joining':
        return 'Joining...';
      default:
        return '';
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const isConnected = () => props.session.sessionState() === 'connected';
  const isActive = () => {
    const s = props.session.sessionState();
    return s === 'connected' || s === 'connecting' || s === 'matched';
  };
  const hasEnded = () => props.session.sessionState() === 'ended';
  const showControls = () => !hasEnded();

  const currentStage = () => props.session.currentStage();

  // Status dot class
  const statusDotClass = () => {
    const state = props.session.sessionState();
    switch (state) {
      case 'connected':
        return styles.statusDotConnected;
      case 'waiting':
        return styles.statusDotWaiting;
      case 'matched':
      case 'connecting':
      case 'joining':
        return styles.statusDotConnecting;
      default:
        return styles.statusDotError;
    }
  };

  // Remote video stage class
  const remoteVideoStageClass = () => {
    if (!isActive() && !isConnected()) return styles.remoteVideoStage1;
    switch (currentStage()) {
      case 1:
        return styles.remoteVideoStage1;
      case 2:
        return styles.remoteVideoStage2;
      case 3:
        return styles.remoteVideoStage3;
      default:
        return styles.remoteVideoStage1;
    }
  };

  // Whether to show avatar (Stage 1 or no connection)
  const showAvatar = () => {
    if (!isConnected()) return false; // Connection overlay handles non-connected states
    return currentStage() === 1;
  };

  // Whether to show filter overlay (Stage 2)
  const showFilterOverlay = () => isConnected() && currentStage() === 2;

  // --- Action handlers ---
  const handleNext = () => {
    setIsFindingNext(true);
    props.session.next();
  };

  const handleEnd = () => {
    props.session.endSession();
    props.onEnd();
  };

  const handleReport = (category: ReportCategory) => {
    props.session.report(category);
    // Auto-disconnect after report
    setTimeout(() => {
      props.session.next();
      setIsFindingNext(true);
    }, 500);
  };

  // Handle "Find Next" from ConnectionStateOverlay when ended
  const handleFindNext = () => {
    // Need to restart the session to find next
    // Since the session is ended, we call onEnd which brings back to landing
    // Instead, we show toast that this requires restarting
    handleEnd();
  };

  // --- Draggable PiP logic ---
  const handlePipPointerDown = (e: PointerEvent) => {
    if (!localPipRef) return;
    e.preventDefault();

    const rect = localPipRef.getBoundingClientRect();
    dragStartOffset = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    setIsDragging(true);
    localPipRef.setPointerCapture(e.pointerId);
  };

  const handlePipPointerMove = (e: PointerEvent) => {
    if (!isDragging() || !localPipRef) return;

    const parentRect = localPipRef.parentElement?.getBoundingClientRect();
    if (!parentRect) return;

    const pipRect = localPipRef.getBoundingClientRect();
    let newX = e.clientX - dragStartOffset.x - parentRect.left;
    let newY = e.clientY - dragStartOffset.y - parentRect.top;

    // Constrain within parent bounds
    newX = Math.max(0, Math.min(parentRect.width - pipRect.width, newX));
    newY = Math.max(0, Math.min(parentRect.height - pipRect.height, newY));

    setPipPosition({ x: newX, y: newY });
  };

  const handlePipPointerUp = () => {
    setIsDragging(false);
  };

  // PiP position styles
  const pipPositionStyle = () => {
    const pos = pipPosition();
    if (!pos) return {};
    return {
      left: `${pos.x}px`,
      top: `${pos.y}px`,
      right: 'auto',
      bottom: 'auto',
    };
  };

  return (
    <div class={styles.page}>
      {/* Toast container */}
      <ToastContainer />

      {/* Stage transition overlay (FE-010) */}
      <StageTransition stageManager={stageManager} />

      {/* Reveal celebration overlay (FE-016) */}
      <RevealCelebration stageManager={stageManager} />

      {/* Video area */}
      <div class={styles.videoArea}>
        {/* Remote video (always in DOM, visibility controlled by stage classes) */}
        <video
          ref={remoteVideoRef}
          autoplay
          playsinline
          class={`${styles.remoteVideo} ${remoteVideoStageClass()} ${styles.stageTransition}`}
          aria-label="Remote participant video"
        />

        {/* Stage 1: Animated avatar silhouette */}
        <div
          class={`${styles.avatarContainer} ${!showAvatar() ? styles.avatarContainerHidden : ''} ${styles.stageTransition}`}
          aria-hidden={!showAvatar()}
        >
          <div style={{ display: 'flex', 'flex-direction': 'column', 'align-items': 'center' }}>
            <div class={styles.avatarSilhouette}>
              <div class={styles.avatarCircle}>
                <IconUser size={56} />
              </div>
              <div
                class={`${styles.audioRing} ${showAvatar() ? styles.audioRingActive : ''}`}
                aria-hidden="true"
              />
            </div>
            <p class={styles.avatarLabel}>Voice only for now</p>
          </div>
        </div>

        {/* Stage 2: Filter overlay effect */}
        <div
          class={`${styles.filterOverlay} ${!showFilterOverlay() ? styles.filterOverlayHidden : ''} ${styles.stageTransition}`}
          aria-hidden="true"
        />

        {/* Local video PiP */}
        <Show when={props.localStream && showControls()}>
          <div
            ref={localPipRef}
            class={`${styles.localPip} ${isDragging() ? styles.localPipDragging : ''}`}
            style={pipPositionStyle()}
            onPointerDown={handlePipPointerDown}
            onPointerMove={handlePipPointerMove}
            onPointerUp={handlePipPointerUp}
            role="img"
            aria-label="Your video preview"
          >
            <Show
              when={!isCameraOff()}
              fallback={
                <div class={styles.localPipPlaceholder}>
                  <IconUser size={32} />
                </div>
              }
            >
              <video
                ref={localVideoRef}
                autoplay
                playsinline
                muted
                class={styles.localVideo}
              />
            </Show>
          </div>
        </Show>

        {/* Reveal button — positioned above bottom controls (FE-016) */}
        <Show when={isConnected()}>
          <div style={{
            position: 'absolute',
            bottom: '120px',
            left: '50%',
            transform: 'translateX(-50%)',
            'z-index': '12',
          }}>
            <RevealButton stageManager={stageManager} />
          </div>
        </Show>

        {/* Connection state overlay */}
        <ConnectionStateOverlay
          sessionState={props.session.sessionState}
          error={props.session.error}
          peerLeft={props.session.peerLeft}
          banInfo={props.session.banInfo}
          rateLimitInfo={props.session.rateLimitInfo}
          onNext={handleFindNext}
          onEnd={handleEnd}
        />
      </div>

      {/* Top bar — status, stage progress, timer */}
      <Show when={showControls()}>
        <div class={styles.topBar}>
          <div class={styles.topBarLeft}>
            <div
              class={`${styles.statusDot} ${statusDotClass()}`}
              role="status"
              aria-label={`Connection: ${stateLabel()}`}
            />
            <span class={styles.stateText}>{stateLabel()}</span>
          </div>

          <div class={styles.topBarCenter}>
            <Show when={isConnected()}>
              <ProgressBar stageManager={stageManager} />
            </Show>
          </div>

          <div class={styles.topBarRight}>
            <Show when={isConnected()}>
              <span class={styles.stageLabel}>
                {stageLabel()}
              </span>
            </Show>
            <span class={styles.timer} aria-label={`Session time: ${formatTime(elapsed())}`}>
              {formatTime(elapsed())}
            </span>
          </div>
        </div>
      </Show>

      {/* Bottom control bar */}
      <Show when={showControls()}>
        <div class={styles.bottomBar}>
          <ControlBar
            isConnected={isConnected}
            isFindingNext={isFindingNext}
            localStream={props.localStream}
            onNext={handleNext}
            onReport={handleReport}
            onEnd={handleEnd}
          />
        </div>
      </Show>

      {/* Debug toggle */}
      <Show when={showControls()}>
        <button
          class={styles.debugToggle}
          onClick={() => setShowDebug(!showDebug())}
          type="button"
          aria-label="Toggle debug panel"
        >
          DBG
        </button>
      </Show>

      {/* Debug panel */}
      <Show when={showDebug()}>
        <div class={styles.debugPanel} role="complementary" aria-label="Debug information">
          <div>state: {props.session.sessionState()}</div>
          <div>stage: {stageManager.currentStage()}</div>
          <div>progress: {(stageManager.stageProgress() * 100).toFixed(1)}%</div>
          <div>reveal: {stageManager.revealState()}</div>
          <div>eligible: {stageManager.isRevealEligible() ? 'yes' : 'no'}</div>
          <div>room: {props.session.roomId() ?? 'none'}</div>
          <div>role: {String(props.session.peerRole() ?? 'none')}</div>
          <div>remote: {props.session.remoteStream() ? 'yes' : 'no'}</div>
          <div>local: {props.localStream ? 'yes' : 'no'}</div>
          <div>elapsed: {formatTime(elapsed())}</div>
          <div>finding: {isFindingNext() ? 'yes' : 'no'}</div>
          <div>total-ms: {stageManager.totalElapsed()}</div>
          <Show when={props.session.error()}>
            <div style={{ color: 'var(--color-error-500)' }}>
              error: {props.session.error()?.code}
            </div>
          </Show>
          <Show when={props.session.banInfo()}>
            <div style={{ color: 'var(--color-error-500)' }}>
              banned: {props.session.banInfo()?.reason}
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
};

export default ChatPage;
